import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import HomePage from "./pages/HomePage";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import AllOrders from "./pages/AllOrders";
import ClientOrders from "./pages/ClientOrders";
import Clients from "./pages/Clients";
import MonthlyOrders from "./pages/MonthlyOrders";
import Users from "./pages/Users";
import MasterData from "./pages/MasterData";
import Attendance from "./pages/Attendance";
import LeaveRequests from "./pages/LeaveRequests";
import Settings from "./pages/Settings";
import SectionTemplates from "./pages/SectionTemplates";
import SalesCRM from "./pages/SalesCRM";
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import HomepageEditor from "./pages/cms/HomepageEditor";
import SiteSettings from "./pages/cms/SiteSettings";
import MediaLibrary from "./pages/cms/MediaLibrary";
import PartnerLogos from "./pages/cms/PartnerLogos";
import BlogList from "./pages/cms/BlogList";
import BlogEditor from "./pages/cms/BlogEditor";
import BlogPreview from "./pages/cms/BlogPreview";
import BlogCategoriesList from "./pages/cms/BlogCategoriesList";
import BlogTagsList from "./pages/cms/BlogTagsList";
import PagesList from "./pages/cms/PagesList";
import PageEditor from "./pages/cms/PageEditor";
import NavigationEditor from "./pages/cms/NavigationEditor";
import FormsList from "./pages/cms/FormsList";
import FormEditor from "./pages/cms/FormEditor";
import FormSubmissions from "./pages/cms/FormSubmissions";
import DynamicPage from "./pages/DynamicPage";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/app" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/category/:slug" element={<BlogIndex />} />
      <Route path="/blog/tag/:slug" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/preview/:token" element={<DynamicPage previewMode={true} />} />
      <Route path="/:slug" element={<DynamicPage />} />
      
      {/* Protected App Routes */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route
          path="all-orders"
          element={
            <ProtectedRoute allowedRoles={["admin", "content_manager"]}>
              <AllOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="monthly"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <MonthlyOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="clients"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Clients />
            </ProtectedRoute>
          }
        />
        <Route
          path="clients/:clientId"
          element={
            <ProtectedRoute allowedRoles={["admin", "content_manager"]}>
              <ClientOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="users"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="masterdata"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <MasterData />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales-crm"
          element={
            <ProtectedRoute allowedRoles={["admin", "bde"]}>
              <SalesCRM />
            </ProtectedRoute>
          }
        />
        <Route path="attendance" element={<Attendance />} />
        <Route
          path="leaves"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <LeaveRequests />
            </ProtectedRoute>
          }
        />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/section-templates" element={<ProtectedRoute allowedRoles={["admin", "content_manager"]}><SectionTemplates /></ProtectedRoute>} />

        {/* CMS Routes (admin + editor) */}
        <Route path="website/homepage" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><HomepageEditor /></ProtectedRoute>} />
        <Route path="website/settings" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><SiteSettings /></ProtectedRoute>} />
        <Route path="website/media" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><MediaLibrary /></ProtectedRoute>} />
        <Route path="website/logos" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><PartnerLogos /></ProtectedRoute>} />
        <Route path="website/blog" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><BlogList /></ProtectedRoute>} />
        <Route path="website/blog/preview/:id" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><BlogPreview /></ProtectedRoute>} />
        <Route path="website/blog/:id" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><BlogEditor /></ProtectedRoute>} />
        <Route path="website/blog-categories" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><BlogCategoriesList /></ProtectedRoute>} />
        <Route path="website/blog-tags" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><BlogTagsList /></ProtectedRoute>} />
        <Route path="website/pages" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><PagesList /></ProtectedRoute>} />
        <Route path="website/pages/:id" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><PageEditor /></ProtectedRoute>} />
        <Route path="website/navigation" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><NavigationEditor /></ProtectedRoute>} />
        <Route path="website/forms" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><FormsList /></ProtectedRoute>} />
        <Route path="website/forms/:id" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><FormEditor /></ProtectedRoute>} />
        <Route path="website/forms/:id/submissions" element={<ProtectedRoute allowedRoles={["admin", "editor"]}><FormSubmissions /></ProtectedRoute>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: 'white',
            border: '1px solid #E2E8F0',
            color: '#1E293B'
          }
        }}
      />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
